import { Mp3Encoder } from "@breezystack/lamejs";

const DEFAULT_BIT_RATE = 64;
const BUFFER_SIZE = 4096;

const toPcm16 = samples => {
  const pcm = new Int16Array(samples.length);

  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return pcm;
};

export const getAudioRecorderErrorKey = error => {
  const errorName = String(error?.name || "");

  if (["NotAllowedError", "PermissionDeniedError"].includes(errorName)) {
    return "messageInput.audio.errors.permissionDenied";
  }

  if (["NotFoundError", "DevicesNotFoundError"].includes(errorName)) {
    return "messageInput.audio.errors.deviceNotFound";
  }

  if (["NotReadableError", "TrackStartError", "AbortError"].includes(errorName)) {
    return "messageInput.audio.errors.deviceUnavailable";
  }

  if (errorName === "NotSupportedError") {
    return "messageInput.audio.errors.notSupported";
  }

  if (errorName === "EmptyAudioError") {
    return "messageInput.audio.errors.emptyAudio";
  }

  return "messageInput.audio.errors.recordingFailed";
};

class AudioRecorderService {
  constructor({ bitRate = DEFAULT_BIT_RATE } = {}) {
    this.bitRate = bitRate;
    this.reset();
  }

  reset() {
    this.audioContext = null;
    this.encoder = null;
    this.encodedChunks = [];
    this.microphone = null;
    this.processor = null;
    this.stream = null;
    this.recording = false;
  }

  async start() {
    if (this.recording) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      const error = new Error("Audio recording is not supported");
      error.name = "NotSupportedError";
      throw error;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      const error = new Error("AudioContext is not supported");
      error.name = "NotSupportedError";
      throw error;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      this.audioContext = new AudioContextClass();
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      this.encoder = new Mp3Encoder(
        1,
        this.audioContext.sampleRate,
        this.bitRate
      );
      this.encodedChunks = [];
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.processor = this.audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);

      this.processor.onaudioprocess = event => {
        if (!this.recording || !this.encoder) {
          return;
        }

        const encoded = this.encoder.encodeBuffer(
          toPcm16(event.inputBuffer.getChannelData(0))
        );

        if (encoded.length > 0) {
          this.encodedChunks.push(new Uint8Array(encoded));
        }
      };

      this.recording = true;
      this.microphone.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
    } catch (error) {
      await this.releaseResources();
      throw error;
    }
  }

  async releaseResources() {
    this.recording = false;

    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
    }

    if (this.microphone) {
      this.microphone.disconnect();
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }

    this.audioContext = null;
    this.microphone = null;
    this.processor = null;
    this.stream = null;
  }

  async stop() {
    if (!this.encoder) {
      const error = new Error("No audio recording is active");
      error.name = "EmptyAudioError";
      throw error;
    }

    await this.releaseResources();

    const finalChunk = this.encoder.flush();
    if (finalChunk.length > 0) {
      this.encodedChunks.push(new Uint8Array(finalChunk));
    }

    const blob = new Blob(this.encodedChunks, { type: "audio/mpeg" });
    this.encoder = null;
    this.encodedChunks = [];

    if (blob.size === 0) {
      const error = new Error("No audio was captured");
      error.name = "EmptyAudioError";
      throw error;
    }

    return blob;
  }

  async cancel() {
    await this.releaseResources();
    this.encoder = null;
    this.encodedChunks = [];
  }
}

export default AudioRecorderService;
